import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const benefits = [
  "Easy to follow — no jargon, no fluff",
  "No expensive equipment needed",
  "Beginner friendly — anyone can start today",
  "Takes only 15 minutes a day",
  "Designed by an experienced doctor",
  "Safe for working professionals & seniors",
];

export default function Benefits({ onCTAClick, stretchingImage }) {
  return (
    <section id="benefits" className="py-20 md:py-28 bg-white" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image left on desktop */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100 via-blue-50 to-orange-50 rounded-[3rem] blur-2xl opacity-60" />
            <div className="relative rounded-[2.5rem] overflow-hidden bg-emerald-50 aspect-[4/5] max-w-md mx-auto shadow-2xl shadow-blue-900/10">
              <img
                src={stretchingImage}
                alt="Person doing back stretching exercises"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating mini badge */}
            <div className="absolute top-6 -right-2 sm:right-6 glass rounded-2xl px-4 py-3 shadow-xl">
              <div className="text-2xl font-black text-emerald-600" style={{ fontFamily: "Outfit" }}>
                15 min
              </div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                Daily Practice
              </div>
            </div>
          </motion.div>

          {/* Copy right */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
              Why This Guide Works
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1E3A8A] leading-tight mb-6" style={{ fontFamily: "Outfit" }}>
              Real results, without the gym & without the guesswork.
            </h2>

            <ul className="space-y-4 mb-8" data-testid="benefits-list">
              {benefits.map((b, idx) => (
                <motion.li
                  key={b}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.07 }}
                  className="flex items-start gap-3"
                >
                  <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                  </div>
                  <span className="text-base sm:text-lg text-slate-700 font-medium">{b}</span>
                </motion.li>
              ))}
            </ul>

            <Button
              onClick={onCTAClick}
              className="h-14 px-8 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-base font-bold cta-glow transition-all hover:-translate-y-0.5"
              data-testid="benefits-cta-button"
            >
              Get the Guide – ₹199
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
